import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

const CHART = {
  gold: '#D4AF37',
  blue: '#60A5FA',
  cyan: '#38BDF8',
  indigo: '#818CF8',
  green: '#10B981',
  red: '#F87171',
  purple: '#A78BFA',
  grid: 'rgba(255,255,255,0.08)',
  axis: '#A0ABC0',
  cardBg: '#112240',
  cardBorder: 'rgba(96, 165, 250, 0.22)',
};

const PIE_COLORS = [CHART.blue, CHART.gold, CHART.purple];

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-3 py-2 text-xs shadow-lg"
      style={{
        backgroundColor: '#0A192F',
        border: '1px solid rgba(96, 165, 250, 0.45)',
        color: '#F8F9FA',
      }}
    >
      {label && <p className="mb-1 font-semibold" style={{ color: '#D4AF37' }}>{label}</p>}
      {payload.map((entry) => (
        <p key={entry.name} style={{ color: entry.color || '#F8F9FA' }}>
          {entry.name}: {typeof entry.value === 'number' ? entry.value.toLocaleString('it-IT') : entry.value}
          {String(entry.name || '').includes('€') || entry.dataKey === 'amount' || entry.dataKey?.includes('revenue') || entry.dataKey?.includes('credits')
            ? ' €'
            : ''}
        </p>
      ))}
    </div>
  );
}

function ChartCard({ title, subtitle, children, className = '', testId }) {
  return (
    <div
      className={`rounded-xl p-4 md:p-5 ${className}`}
      style={{ backgroundColor: CHART.cardBg, border: `1px solid ${CHART.cardBorder}` }}
      data-testid={testId}
    >
      <div className="mb-4">
        <h3
          className="text-sm md:text-base font-semibold"
          style={{ color: '#F8F9FA', fontFamily: 'Playfair Display, serif' }}
        >
          {title}
        </h3>
        {subtitle && (
          <p className="text-xs mt-1" style={{ color: '#A0ABC0' }}>
            {subtitle}
          </p>
        )}
      </div>
      <div className="h-64 md:h-72">{children}</div>
    </div>
  );
}

const axisProps = {
  stroke: CHART.axis,
  tick: { fill: CHART.axis, fontSize: 11 },
  tickLine: false,
  axisLine: { stroke: CHART.grid },
};

export function AdminAnalyticsPanel({ data, isLoading, generatedAt }) {
  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="spinner" />
      </div>
    );
  }

  const dailyLeads = data?.daily_leads || [];
  const lawyerMonthly = data?.lawyer_monthly || [];
  const leadsByCategory = data?.leads_by_category || [];
  const topSpenders = data?.top_spenders || [];
  const financialTrend = data?.financial_trend || [];
  const revenueBreakdown = data?.revenue_breakdown || [];

  const updatedLabel = generatedAt
    ? new Date(generatedAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })
    : '—';

  return (
    <div className="space-y-4" data-testid="admin-analytics-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <p className="text-xs" style={{ color: '#64748B' }}>
          Dati aggiornati: {updatedLabel}
        </p>
        <p className="text-xs" style={{ color: '#64748B' }}>
          Auto-refresh ogni 60 secondi
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ChartCard
          title="Andamento Lead Giornalieri"
          subtitle="Lead inseriti nel mese corrente"
          testId="chart-daily-leads"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyLeads} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip content={<ChartTooltip />} />
              <Line
                type="monotone"
                dataKey="count"
                name="Lead"
                stroke={CHART.cyan}
                strokeWidth={2.5}
                dot={{ r: 3, fill: CHART.cyan, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: CHART.blue }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Crescita e Abbandono Avvocati"
          subtitle="Nuovi iscritti vs sospensioni per mese"
          testId="chart-lawyer-growth"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={lawyerMonthly} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="label" {...axisProps} interval={0} angle={-25} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} {...axisProps} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ color: CHART.axis, fontSize: 12 }} />
              <Bar dataKey="new_lawyers" name="Nuovi iscritti" fill={CHART.green} radius={[4, 4, 0, 0]} />
              <Bar dataKey="churn_lawyers" name="Sospesi / Churn" fill={CHART.red} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Distribuzione Lead per Categoria"
          subtitle="Volume per tipologia di incidente"
          testId="chart-lead-categories"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={leadsByCategory}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                stroke="#0A192F"
              >
                {leadsByCategory.map((entry, index) => (
                  <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ color: CHART.axis, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Top Spender — Consumo Crediti"
          subtitle="Euro scalati negli ultimi 30 giorni"
          testId="chart-top-spenders"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={topSpenders}
              layout="vertical"
              margin={{ top: 8, right: 16, left: 8, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
              <XAxis type="number" {...axisProps} />
              <YAxis type="category" dataKey="name" width={100} {...axisProps} tick={{ fill: CHART.axis, fontSize: 10 }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="amount" name="Speso" fill={CHART.gold} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Mix Fatturato Stimato"
          subtitle="Canoni fissi vs lead extra — mese corrente (imponibile)"
          testId="chart-revenue-breakdown"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={revenueBreakdown.length ? revenueBreakdown : [{ name: 'Nessun dato', value: 1 }]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={3}
                stroke="#0A192F"
              >
                {(revenueBreakdown.length ? revenueBreakdown : [{ name: 'Nessun dato', value: 1 }]).map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={entry.name === 'Canoni fissi' ? CHART.gold : entry.name === 'Lead extra' ? CHART.blue : CHART.purple}
                    opacity={revenueBreakdown.length ? 1 : 0.35}
                  />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ color: CHART.axis, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Fatturato Storico vs Crediti Attivi"
          subtitle="Confronto cumulativo ultimi 30 giorni"
          className="xl:col-span-2"
          testId="chart-financial-trend"
        >
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={financialTrend} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
              <defs>
                <linearGradient id="revGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART.blue} stopOpacity={0.45} />
                  <stop offset="95%" stopColor={CHART.blue} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="credGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART.gold} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={CHART.gold} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} />
              <XAxis dataKey="label" {...axisProps} />
              <YAxis {...axisProps} />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ color: CHART.axis, fontSize: 12 }} />
              <Area
                type="monotone"
                dataKey="revenue_cumulative"
                name="Totale incassato"
                stroke={CHART.blue}
                fill="url(#revGradient)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="credits_active"
                name="Crediti attivi"
                stroke={CHART.gold}
                fill="url(#credGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}
